import React from 'react';
import { Cpu, HardDrive, MemoryStick } from 'lucide-react';
import { mergeGatewayCompute } from '../../utils/gatewayCompute';
import {
  getGatewayStatus,
  getGatewayPlatform,
  getComputeColor,
  getTimeSinceLastEvent,
  PLATFORM_DISPLAY_NAMES,
} from '../../utils/gatewayHelpers';
import type { DeviceStats } from '../../types';

interface GatewayRowProps {
  stat: DeviceStats;
  onSelect: (stat: DeviceStats) => void;
}

const STATUS_STYLE: Record<'active' | 'offline' | 'unknown', { color: string; bg: string; border: string }> = {
  active:  { color: '#86efac',                  bg: 'rgba(34, 197, 94, 0.1)',    border: 'rgba(34, 197, 94, 0.3)'    },
  offline: { color: '#fca5a5',                  bg: 'rgba(248, 113, 113, 0.1)',  border: 'rgba(248, 113, 113, 0.3)'  },
  unknown: { color: 'rgba(156, 163, 175, 0.8)', bg: 'rgba(156, 163, 175, 0.1)', border: 'rgba(156, 163, 175, 0.2)'  },
};

const TIME_COLOR: Record<'ok' | 'warning' | 'critical', string> = {
  ok:       '#86efac',
  warning:  '#fbbf24',
  critical: '#fca5a5',
};

function ComputeMetrics({ stat }: { stat: DeviceStats }) {
  const compute = mergeGatewayCompute(stat.stats as Record<string, unknown>);
  const cpu  = compute?.cpu?.usage_percent;
  const mem  = compute?.memory?.usage_percent;
  const disk = compute?.disk?.usage_percent;

  if (cpu == null && mem == null && disk == null) {
    return <span className="text-xs" style={{ color: 'rgba(255,255,255,0.25)' }}>—</span>;
  }

  return (
    <>
      {[
        { icon: <Cpu className="h-3 w-3" />,         label: 'CPU',  val: cpu  },
        { icon: <MemoryStick className="h-3 w-3" />, label: 'RAM',  val: mem  },
        { icon: <HardDrive className="h-3 w-3" />,   label: 'Disk', val: disk },
      ].map(({ icon, label, val }) => {
        const color = getComputeColor(val);
        return (
          <div key={label} className="flex items-center gap-1.5">
            <span style={{ color }}>{icon}</span>
            <span className="text-xs w-7" style={{ color: 'rgba(255,255,255,0.45)' }}>{label}</span>
            <div className="flex-1 rounded-full h-1.5" style={{ backgroundColor: 'rgba(255,255,255,0.07)', minWidth: 40 }}>
              <div className="h-1.5 rounded-full" style={{ width: `${Math.min(val ?? 0, 100)}%`, backgroundColor: color }} />
            </div>
            <span className="text-xs font-medium w-10 text-right" style={{ color }}>
              {val != null ? `${val.toFixed(0)}%` : '—'}
            </span>
          </div>
        );
      })}
    </>
  );
}

export function GatewayRow({ stat, onSelect }: GatewayRowProps) {
  const status    = getGatewayStatus(stat);
  const platform  = getGatewayPlatform(stat);
  const timeSince = getTimeSinceLastEvent(stat);
  const style     = STATUS_STYLE[status];
  const timeColor = TIME_COLOR[timeSince.status];

  return (
    <div
      className="rounded-xl px-5 py-4 transition-all duration-200 hover:scale-[1.01]"
      style={{ backgroundColor: 'rgba(255, 255, 255, 0.03)', border: `1px solid ${style.border}` }}
    >
      <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1.6fr)_minmax(0,0.8fr)_minmax(0,1fr)_minmax(0,0.6fr)] gap-4 items-center">
        {/* Identity */}
        <div className="min-w-0">
          {status === 'active' ? (
            <button
              onClick={() => onSelect(stat)}
              className="font-medium text-sm truncate text-left hover:underline"
              style={{ color: '#5eead4', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            >
              {stat.site_id}
            </button>
          ) : (
            <div className="font-medium text-sm truncate" style={{ color: '#ffffff' }}>
              {stat.site_id}
            </div>
          )}
          <div className="text-xs mt-1 truncate" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
            UUID: {stat.uuid}
          </div>
        </div>

        {/* Status + Platform */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: style.color }} />
          <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: style.bg, color: style.color }}>
            {status.toUpperCase()}
          </span>
          <span
            className="text-xs px-2 py-1 rounded-full"
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', color: 'rgba(255, 255, 255, 0.7)' }}
          >
            {PLATFORM_DISPLAY_NAMES[platform] || platform}
          </span>
        </div>

        {/* Compute metrics */}
        <div className="flex flex-col gap-1.5">
          {status !== 'active'
            ? <span className="text-xs" style={{ color: 'rgba(255,255,255,0.25)' }}>—</span>
            : <ComputeMetrics stat={stat} />
          }
        </div>

        {/* Last seen */}
        <div className="text-left">
          <div className="text-xs font-medium" style={{ color: timeColor }}>{timeSince.text}</div>
          <div className="text-xs mt-1" style={{ color: 'rgba(255, 255, 255, 0.4)' }}>
            {new Date(stat.event_received_time).toLocaleString()}
          </div>
        </div>
      </div>
    </div>
  );
}
